import { NativeVideoDiagnostics, validDiagnostics } from './NativeVideoDiagnostics';
/** Reliable signaling carried by the already authenticated Electron peer. */
export const NATIVE_VIDEO_CHANNEL = 'playcast-native-video-v1';
export type NativeVideoCodec = 'h264' | 'hevc' | 'av1';
export type NativeVideoPlayback = 'rtp' | 'webcodecs';
export type NativeVideoSource = 'shadowcam' | 'stream';
export type NativeVideoSettings = {
  backend: 'electron' | 'native';
  codec: NativeVideoCodec;
  playback?: NativeVideoPlayback;
  source?: NativeVideoSource;
};
/** `safe30` caps cadence only; the `<height>p<fps>` modes are ceilings PlayJector clamps the camera preset to. */
export const NATIVE_VIDEO_MODES = ['safe30', '1080p120', '1080p60', '720p120', '720p60'] as const;
export type NativeVideoMode = (typeof NATIVE_VIDEO_MODES)[number];
export type NativeVideoMessage = {
  nativeVideo: 1;
  type: 'hello' | 'configure' | 'offer' | 'answer' | 'ice' | 'ready' | 'fallback' | 'close' | 'diagnostics';
  diagnostics?: NativeVideoDiagnostics;
  diagnosticsEnabled?: boolean;
  session?: string;
  codec?: NativeVideoCodec;
  playback?: NativeVideoPlayback;
  webCodecs?: { version: 1; codecs: NativeVideoCodec[] };
  configEpoch?: number;
  firstFrameId?: number;
  sdp?: string;
  candidate?: RTCIceCandidateInit;
  videoMode?: NativeVideoMode;
  reason?: string;
  source?: NativeVideoSource;
};

/** Reject oversized or unrelated input before it reaches a native session. */
export function parseNativeVideoMessage(data: unknown): NativeVideoMessage | null {
  if (typeof data !== 'string' || data.length > 60000) return null;
  try {
    const m = JSON.parse(data);
    if (m?.nativeVideo !== 1 || !['hello', 'configure', 'offer', 'answer', 'ice', 'ready', 'fallback', 'close', 'diagnostics'].includes(m.type))
      return null;
    if (m.type !== 'hello' && (typeof m.session !== 'string' || !/^native-player-[a-zA-Z0-9-]{1,80}$/.test(m.session))) return null;
    if (m.codec !== undefined && !['h264', 'hevc', 'av1'].includes(m.codec)) return null;
    if (!validPlaybackFields(m)) return null;
    if (['offer', 'answer'].includes(m.type) && (typeof m.sdp !== 'string' || !m.sdp.startsWith('v=0'))) return null;
    if (m.type === 'configure' && !m.codec) return null;
    if (m.source !== undefined && (m.type !== 'configure' || (m.source !== 'shadowcam' && m.source !== 'stream'))) return null;
    if (m.type === 'ice' && (!m.candidate || typeof m.candidate.candidate !== 'string' || m.candidate.candidate.length > 4096)) return null;
    if (m.videoMode !== undefined && !(NATIVE_VIDEO_MODES as readonly string[]).includes(m.videoMode)) return null;
    if (m.reason !== undefined && (typeof m.reason !== 'string' || m.reason.length > 512)) return null;
    if (m.diagnosticsEnabled !== undefined && typeof m.diagnosticsEnabled !== 'boolean') return null;
    if (m.type === 'diagnostics' && !validDiagnostics(m.diagnostics)) return null;
    return m;
  } catch {
    return null;
  }
}

/** Shape validation only; the host must bind hello capabilities to its authenticated peer. */
function validPlaybackFields(m: NativeVideoMessage): boolean {
  if (m.playback !== undefined && m.playback !== 'rtp' && m.playback !== 'webcodecs') return false;
  if (m.type === 'hello' && m.playback === 'webcodecs' && m.webCodecs === undefined) return false;
  if (m.webCodecs !== undefined) {
    const capability = m.webCodecs;
    if (m.type !== 'hello' || !capability || Object.keys(capability).length !== 2 || capability.version !== 1 ||
        !Array.isArray(capability.codecs) || capability.codecs.length < 1 || capability.codecs.length > 3 ||
        capability.codecs.some(codec => !['h264', 'hevc', 'av1'].includes(codec)) ||
        new Set(capability.codecs).size !== capability.codecs.length) return false;
  }
  for (const id of [m.configEpoch, m.firstFrameId]) {
    if (id !== undefined && (m.type !== 'ready' || m.playback !== 'webcodecs' ||
        !Number.isInteger(id) || id < 1 || id > 0xffffffff)) return false;
  }
  return m.type !== 'ready' || m.playback !== 'webcodecs' ||
    (m.configEpoch !== undefined && m.firstFrameId !== undefined);
}

/** Host preferences default to the existing Electron video backend. */
export function readNativeVideoSettings(storage?: Pick<Storage, 'getItem'>): NativeVideoSettings {
  try {
    const value = JSON.parse(storage?.getItem('playcast.videoBackend') ?? '{}');
    const backend = value.backend === 'native' ? 'native' : 'electron';
    return {
      backend,
      codec: ['h264', 'hevc', 'av1'].includes(value.codec) ? value.codec : 'h264',
      ...(value.playback === 'rtp' || value.playback === 'webcodecs'
        ? { playback: value.playback }
        : backend === 'native' ? { playback: 'webcodecs' as const } : {}),
      ...(value.source === 'shadowcam' || value.source === 'stream' ? { source: value.source } : {}),
    };
  } catch {
    return { backend: 'electron', codec: 'h264' };
  }
}
